import numeral from 'numeral';
import { ILongDisclaimerData, IShortDisclaimerData } from '../interfaces';
import { validateDomain, validateEmail } from './validator';

// Exported Functions

export function roundDollar(dollars: number) {
    return Math.ceil(dollars);
}

export function isValidNumber(validNumber: number) {
    return !(validNumber === null || isNaN(validNumber));
}

export function isValidDollarAmount(dollars: number) {
    return isValidNumber(dollars);
}

export function invalidDollarDisplayValue() {
    return `$${invalidNumberDisplayValue()}`;
}

export function invalidNumberDisplayValue() {
    return '--';
}

export function formatDollars(dollars, defaultString?) {
    if (isValidNumber(dollars)) {
        return numeral(roundDollar(dollars)).format('$0,000');
    }
    return defaultString ? defaultString : invalidDollarDisplayValue();
}

export function formatDollarsAndCents(dollars, defaultString?) {
    if (isValidNumber(dollars)) {
        return numeral(dollars).format('$0,000.00');
    }
    return defaultString ? defaultString : invalidDollarDisplayValue();
}

export function formatMonthlyPayment(dollars, defaultString?) {
    if (isValidNumber(dollars)) {
        const dollarsRounded = Math.round(dollars);
        return numeral(dollarsRounded).format('$0,000');
    }
    return defaultString ? defaultString : invalidDollarDisplayValue();
}

export function formatNumber(numberNeedingFormat: number) {
    return numeral(numberNeedingFormat).format('0,000');
}

export function formatNumberAndCents(number, defaultString?) {
    if (isValidNumber(number)) {
        return numeral(number).format('0,000.00');
    }
    return defaultString ? defaultString : invalidNumberDisplayValue();
}

export function formatNumberDecimals(number, defaultString?) {
    if (isValidNumber(number)) {
        return numeral(number).format('0.00');
    }
    return defaultString ? defaultString : invalidNumberDisplayValue();
}

export function formatTerm(term: number | string, treatNullOrUndefinedAsInvalid: boolean = true): string {
    if (!term && term !== 0 && term !== '0') {
        return treatNullOrUndefinedAsInvalid ? invalidNumberDisplayValue() : '';
    }
    return `${term}`;
}

export function formatMiles(value: number) {
    const formattedNumber = formatNumber(value);
    return `${formattedNumber} mi`;
}

export function formatCurrencyAmount(amount: number) {
    return isValidDollarAmount(amount) ? formatDollars(amount) : invalidDollarDisplayValue();
}

export function formatAPR(apr: number): string {
    if (isNaN(apr)) {
        return '-';
    }
    return numeral(apr).format('0.00');
}

/**
 * Returns a formatted string for the monthly payment, with a range if applicable
 * @param monthlyPayment
 * @param range
 * @returns formatted string
 * @example formatMonthlyPaymentRange(100, 50) => "$100 - $150"
 * @example formatMonthlyPaymentRange(100, 0) => "$100"
 * @example formatMonthlyPaymentRange(100) => "$100"
 */
export function formatMonthlyPaymentRange(monthlyPayment: number | string, range?: number): string {
    if (range > 0) {
        const formattedMonthlyPayment = formatMonthlyPayment(monthlyPayment);
        return `${formattedMonthlyPayment} - ${formatMonthlyPayment(range + Number(monthlyPayment))}`;
    }
    return formatMonthlyPayment(monthlyPayment);
}

export function formatDate(date: Date) {
    const finalDay = isNaN(date.getTime()) ? new Date() : date;
    const month = finalDay.getMonth() + 1 > 9 ? finalDay.getMonth() + 1 : `0${finalDay.getMonth() + 1}`;
    const day = finalDay.getDate() > 9 ? finalDay.getDate() : `0${finalDay.getDate()}`;
    return `${month}/${day}/${finalDay.getFullYear()}`;
}

// One-Pay Lease (OPL) disclaimer copy.
// `data.monthlyPayment` carries the single-payment (Due-at-Signing) amount for OPL.
const CLOSED_LEASE_SUFFIX = ' This is a closed lease.';

function formatOnePayLeaseLongDisclaimer(data: ILongDisclaimerData): string {
    const offerPrice = formatDollars(data.offerPrice);
    const annualMiles = formatNumber(data.annualMiles);
    const termMonths = `${data.termMonths} months`;
    const acqLoanFee = formatDollars(data.acqLoanFee);
    const capCostTotal = formatDollars(data.capCostTotal);
    const onePayAmount = formatDollars(data.monthlyPayment);
    const securityDeposit = formatDollars(data.securityDeposit);
    const excessMileageCharge = formatDollarsAndCents(data.excessMileageCharge);
    const availability = data.programEndDate
        ? ` Available at participating dealers by ${formatDate(new Date(data.programEndDate))}.`
        : '';

    return (
        `*Estimated one-pay lease payment based on your offer price of ${offerPrice} at ${annualMiles} miles/year for ${termMonths} ` +
        `with ${acqLoanFee} acquisition fee, including the gross capitalized cost of ${capCostTotal}. ` +
        `${data.cludes} tax, title, registration, and license. ` +
        `This is a one-pay lease; the full lease payment of ${onePayAmount} is paid in a single amount at signing. ` +
        `No additional monthly payments are due. Due at signing of ${onePayAmount} includes the capitalized cost reduction, ` +
        `a required security deposit of ${securityDeposit}, and the single lease payment. ` +
        `${excessMileageCharge} charge per mile over ${annualMiles} miles per year. For qualified buyers. ` +
        `Contingent upon dealer's acceptance of these terms.${availability} ` +
        `Early termination fees may apply. Lessee is responsible for maintenance, insurance, repair, and excess wear/tear at end of lease. ` +
        `Lessee will not be liable for extra charges if realized vehicle value is lower than residual value at end of lease. ` +
        `See ${data.dealerName} for complete details on this and other offers.${CLOSED_LEASE_SUFFIX}`
    );
}

function formatOnePayLeaseShortDisclaimer(data: IShortDisclaimerData): string {
    const offerPrice = formatDollars(data.offerPrice);
    const annualMiles = formatNumber(data.annualMiles);
    const termMonths = `${data.termMonths} months`;
    const acqLoanFee = formatDollars(data.acqLoanFee);
    const capCostTotal = formatDollars(data.capCostTotal);

    return (
        `*Estimated one-pay lease payment based on your offer price of ${offerPrice} at ${annualMiles} miles/year for ${termMonths} ` +
        `with ${acqLoanFee} acquisition fee, including the gross capitalized cost of ${capCostTotal}. ` +
        `${data.cludes} tax, title, registration, and license. This is a one-pay lease.`
    );
}

export function formatLongDisclaimer(data: ILongDisclaimerData) {
    const { boldDealValues = false } = data;
    const downPayment = boldDealValues ? `<b>${formatDollars(data.downPayment)}</b>` : formatDollars(data.downPayment);
    const offerPrice = boldDealValues ? `<b>${formatDollars(data.offerPrice)}</b>` : formatDollars(data.offerPrice);
    const totalPaymentAmount = boldDealValues
        ? `<b>${formatDollars(data.totalPaymentAmount)}</b>`
        : formatDollars(data.totalPaymentAmount);
    const termMonths = boldDealValues ? `<b>${data.termMonths} months</b>` : `${data.termMonths} months`;

    let disclaimerText = `*Estimated payment based on your offer price of `;
    if (data.offerType === 'finance') {
        const monthlyAprCost = boldDealValues
            ? `<b>${formatDollarsAndCents(data.monthlyAprCost)}/month</b>`
            : `${formatDollarsAndCents(data.monthlyAprCost)}/month`;
        disclaimerText += `${offerPrice}`;
        if (!data.hideAPR) {
            const apr = boldDealValues ? `<b>${data.apr}%</b>` : `${data.apr}%`;
            disclaimerText += ` at ${apr} APR`;
        }
        disclaimerText += ` for ${termMonths} at ${monthlyAprCost}, per $1,000 financed, ${downPayment} down for qualified buyers, ${totalPaymentAmount} total of all payments. ${data.cludes} tax, title, registration, and license. Contingent upon dealer’s acceptance of these terms. Excludes leases and balloon contracts. Available at participating dealers by ${data.lender}. Offer may expire at any time without notification. See dealer for details. Subject to credit approval by lender. Rate applies to vehicle shown. Not everyone will qualify.`;
    } else if (data.offerType === 'lease') {
        // One-Pay Lease disclaimer
        if (data.isOnePayLease) {
            return formatOnePayLeaseLongDisclaimer(data);
        }
        const annualMiles = formatNumber(data.annualMiles);
        const monthlyPayment = formatMonthlyPaymentRange(data.monthlyPayment, data.paymentRange);
        const acqLoanFee = formatDollars(data.acqLoanFee);
        const capCostTotal = formatDollars(data.capCostTotal);
        const capCostReduction = formatDollars(data.capCostReduction);
        const securityDeposit = formatDollars(data.securityDeposit);
        const excessMileageCharge = formatDollarsAndCents(data.excessMileageCharge);
        const downPaymentQualifier = data.deductDueAtSigningDealerPref ? 'due at signing' : 'down';
        const cashDownPaymentQualifier = data.deductDueAtSigningDealerPref ? 'Due at signing' : 'Cash down';
        disclaimerText = `*Estimated payment based on your offer price of `;

        disclaimerText += `${offerPrice} at ${annualMiles} miles/year for ${termMonths} with ${downPayment} ${downPaymentQualifier}, ${acqLoanFee} acquisition fee, including the gross capitalized cost of ${capCostTotal}. ${data.cludes} tax, title, registration, and license. Total monthly payments equal ${totalPaymentAmount}. ${cashDownPaymentQualifier} includes ${capCostReduction} capitalized cost reduction, a required security deposit of ${securityDeposit}, and first month’s lease payment of ${monthlyPayment}. ${excessMileageCharge} charge per mile over ${annualMiles} miles per year. For qualified buyers. Contingent upon dealer’s acceptance of these terms. Available at participating dealers by ${data.lender}. Early termination fees may apply. Lessee is responsible for maintenance, insurance, repair, and excess wear/tear at end of lease. Lessee will not be liable for extra charges if realized vehicle value is lower than residual value at end of lease. Lessee may incur penalties and additional charges for delinquent payments. See ${data.dealerName} for complete details on this and other offers.`;
    } else {
        disclaimerText += `${offerPrice}. `;
        if (data.taxesAndFeesTotal) {
            disclaimerText += `${data.cludes} ${formatDollars(data.taxesAndFeesTotal)} in fees`;
            const includesTaxes = data.totalTax ? ` and taxes. ` : `. `;

            disclaimerText += `${includesTaxes}Final transaction may include additional fees/charges and taxes. `;
        }
        disclaimerText += `This is not an offer or sales contract and is contingent upon dealer’s acceptance of these terms. See dealer for terms of purchase.`;
    }

    if (data.programEndDate && data.offerType !== 'cash') {
        const programEndDate = formatDate(new Date(data.programEndDate));
        disclaimerText = `${disclaimerText} Payment Expires: ${programEndDate}.`;
    }

    return data.offerType === 'lease' ? `${disclaimerText}${CLOSED_LEASE_SUFFIX}` : disclaimerText;
}

export function formatShortDisclaimer(data: IShortDisclaimerData) {
    const downPayment = formatDollars(data.downPayment);
    const offerPrice = formatDollars(data.offerPrice);

    let disclaimerText = '';
    if (data.offerType === 'finance') {
        disclaimerText = `*Est. pmt. based on your offer price of `;

        disclaimerText += `${offerPrice}`;
        if (!data.hideAPR) {
            disclaimerText += ` at ${data.apr}% APR`;
        }
        disclaimerText += ` for ${data.termMonths} mos w/ ${downPayment} down for qualified buyers. Contingent upon dealer’s acceptance of these terms. Terms may vary.`;
    } else if (data.offerType === 'lease') {
        // One-Pay Lease disclaimer
        if (data.isOnePayLease) {
            return formatOnePayLeaseShortDisclaimer(data);
        }
        const annualMiles = formatNumber(data.annualMiles);
        const downPaymentQualifier = data.deductDueAtSigningDealerPref ? 'due at signing' : 'down';

        disclaimerText = `*Est. pmt. based on your offer price of `;

        disclaimerText += `${offerPrice} at ${annualMiles} mi/yr for ${data.termMonths} mos with ${downPayment} ${downPaymentQualifier} for qualified buyers. Contingent upon dealer’s acceptance of these terms. Terms may vary.`;
    } else {
        disclaimerText = 'Final transaction may include title, registration, license and other additional fees/charges.';
    }

    if (data.excludeTaxesAndFeeOnUnlockedDS) {
        disclaimerText += ' Taxes and dealer fees not included.';
    }

    if (data.programEndDate && data.offerType !== 'cash') {
        const programEndDate = formatDate(new Date(data.programEndDate));
        disclaimerText = `${disclaimerText} Payment Expires: ${programEndDate}.`;
    }

    return disclaimerText;
}

// Redesign Disclaimers
export function formatLongDisclaimerRedesign(data: ILongDisclaimerData) {
    const downPayment = formatDollars(data.downPayment);
    const offerPrice = formatDollars(data.offerPrice);
    const totalPaymentAmount = formatDollars(data.totalPaymentAmount);
    const taxesAndFeesDisclaimer = getTaxesAndFeesDisclaimer(data);

    let disclaimerText = `*Estimated ${data.offerType === 'finance' ? 'monthly ' : ''}payment based on your offer price of `;

    // For CDD in cases where financialDetails.program.partnerName
    // is blank it makes us unable to pull the offer up in SP so it's
    // defaulted to 'fakedName'
    const isLenderPopulated = data.lender && data.lender !== 'fakedName';

    if (data.offerType === 'finance') {
        const monthlyAprCost = formatDollarsAndCents(data.monthlyAprCost);

        const offerPriceChange = data.hasOfferPriceChanged ? `Subject to acceptance by dealer of your modified terms. ` : '';
        const lenderAvailibilityText = isLenderPopulated
            ? `and is subject to credit approval by ${data.lender}.`
            : 'and is subject to credit approval.';

        disclaimerText += `${offerPrice}`;
        if (!data.hideAPR) {
            disclaimerText += ` at ${data.apr}% APR`;
        }
        disclaimerText += ` for ${data.termMonths} months at ${monthlyAprCost}/month, per $1,000 financed, ${downPayment} down for qualified buyers, ${totalPaymentAmount} total of all payments. ${taxesAndFeesDisclaimer} ${offerPriceChange}Excludes leases and balloon contracts. Available at participating dealers ${lenderAvailibilityText} Offer may expire at any time without notification. See dealer for details. Rate applies to vehicle shown. Not everyone will qualify. `;
    } else if (data.offerType === 'lease') {
        // One-Pay Lease disclaimer
        if (data.isOnePayLease) {
            return formatOnePayLeaseLongDisclaimer(data);
        }
        const annualMiles = formatNumber(data.annualMiles);
        const monthlyPayment = formatMonthlyPaymentRange(data.monthlyPayment, data.paymentRange);
        const acqLoanFee = formatDollars(data.acqLoanFee);
        const capCostTotal = formatDollars(data.capCostTotal);
        const capCostReduction = formatDollars(data.capCostReduction);
        const securityDeposit = formatDollars(data.securityDeposit);
        const excessMileageCharge = formatDollarsAndCents(data.excessMileageCharge);
        const isModifiedTerms = data.hasOfferPriceChanged ? ` Subject to acceptance by dealer of your modified terms.` : '';
        const downPaymentQualifier = data.deductDueAtSigningDealerPref ? 'due at signing' : 'down';
        const cashDownPaymentQualifier = data.deductDueAtSigningDealerPref ? 'Due at signing' : 'Cash down';

        const lenderAvailibilityText = isLenderPopulated
            ? `by ${data.lender} at participating dealers.`
            : 'at participating dealers.';

        disclaimerText = `*Estimated monthly payment based on your offer price of `;

        disclaimerText += `${offerPrice} at ${annualMiles} miles/year for ${data.termMonths} months with ${downPayment} ${downPaymentQualifier}, ${acqLoanFee} acquisition fee, including the gross capitalized cost of ${capCostTotal}. ${taxesAndFeesDisclaimer} Total monthly payments equal ${totalPaymentAmount}. ${cashDownPaymentQualifier} includes ${capCostReduction} capitalized cost reduction, a required security deposit of ${securityDeposit}, and first month’s lease payment of ${monthlyPayment}. ${excessMileageCharge} charge per mile over ${annualMiles} miles per year. For qualified buyers.${isModifiedTerms} Available to approved lessees ${lenderAvailibilityText} Early termination fees may apply. Lessee is responsible for maintenance, insurance, repair, and excess wear/tear at end of lease. Lessee will not be liable for extra charges if realized vehicle value is lower than residual value at end of lease. Lessee may incur penalties and additional charges for delinquent payments. See ${data.dealerName} for complete details on this and other offers. `;
    } else {
        disclaimerText += `${offerPrice}. `;
        if (data.taxesAndFeesTotal) {
            disclaimerText += `Includes ${formatDollars(data.taxesAndFeesTotal)} in fees`;
            const includesTaxes = data.totalTax ? ` and taxes. ` : `. `;

            disclaimerText += `${includesTaxes}Final transaction may include taxes, title, registration, license and other additional fees/charges. `;
        }
        if (data.feesDisclaimer) {
            disclaimerText += `${data.feesDisclaimer} `;
        }

        const modifiedTerms = data.hasOfferPriceChanged ? ` and is subject to acceptance by dealer of your modified terms` : '';
        disclaimerText += `This is not an offer or sales contract${modifiedTerms}. See dealer for terms of purchase.`;
    }

    if (data.programEndDate && data.offerType !== 'cash') {
        const programEndDate = formatDate(new Date(data.programEndDate));
        disclaimerText = `${disclaimerText}Available through ${programEndDate}.`;
    }

    return data.offerType === 'lease' ? `${disclaimerText}${CLOSED_LEASE_SUFFIX}` : disclaimerText;
}

export function formatShortDisclaimerRedesign(data: IShortDisclaimerData) {
    const downPayment = formatDollars(data.downPayment);
    const offerPrice = formatDollars(data.offerPrice);
    const offerPriceChange = data.hasOfferPriceChanged ? `Subject to acceptance by dealer of your modified terms. ` : '';

    let disclaimerText = '';

    if (data.offerType === 'finance') {
        disclaimerText = `*Est. mo. pmt. based on your offer price of ${offerPrice}`;
        if (!data.hideAPR) {
            disclaimerText += ` at ${data.apr}% APR`;
        }
        disclaimerText += ` for ${data.termMonths} mos w/ ${downPayment} down for qualified buyers. ${offerPriceChange}Terms may vary.`;
    } else if (data.offerType === 'lease') {
        // One-Pay Lease disclaimer
        if (data.isOnePayLease) {
            return formatOnePayLeaseShortDisclaimer(data);
        }
        const annualMiles = formatNumber(data.annualMiles);
        const downPaymentQualifier = data.deductDueAtSigningDealerPref ? 'due at signing' : 'down';
        disclaimerText = `*Est. mo pmt. based on your offer price of `;

        disclaimerText += `${offerPrice} at ${annualMiles} mi/yr for ${data.termMonths} mos with ${downPayment} ${downPaymentQualifier} for qualified buyers. ${offerPriceChange}Terms may vary.`;
    } else {
        disclaimerText = 'Final transaction may include title, registration, license and other additional fees/charges.';
    }

    if (data.excludeTaxesAndFeeOnUnlockedDS) {
        disclaimerText += ' Taxes and dealer fees not included.';
    }

    if (data.programEndDate && data.offerType !== 'cash') {
        const programEndDate = formatDate(new Date(data.programEndDate));
        disclaimerText = `${disclaimerText} Available through ${programEndDate}.`;
    }

    return disclaimerText;
}

export function formatShortDisclaimerImprovedWorkflow(data: IShortDisclaimerData) {
    const downPayment = data.boldDealValues ? `<b>${formatDollars(data.downPayment)}</b>` : formatDollars(data.downPayment);
    const offerPrice = data.boldDealValues ? `<b>${formatDollars(data.offerPrice)}</b>` : formatDollars(data.offerPrice);
    const offerPriceChange = data.hasOfferPriceChanged ? `Subject to acceptance by dealer of your modified terms. ` : '';
    const formatterApr = data.boldDealValues ? ` <b>${data.apr}%</b>` : `${data.apr}%`;
    const formattedTermMonths = data.boldDealValues ? `<b>${data.termMonths} months</b>` : `${data.termMonths} months`;
    const formattedDownPayment = data.boldDealValues ? `<b>${downPayment}</b>` : downPayment;

    let disclaimerText = '';

    if (data.offerType === 'finance') {
        disclaimerText = `*Estimated monthly payment based on your offer price of ${offerPrice} at ${formatterApr} APR for ${formattedTermMonths} with ${formattedDownPayment} down for qualified buyers. ${offerPriceChange}Terms may vary.`;
    } else if (data.offerType === 'lease') {
        const annualMiles = formatNumber(data.annualMiles);
        const downPaymentQualifier = data.deductDueAtSigningDealerPref ? 'due at signing' : 'down';

        disclaimerText = `*Estimated monthly payment based on your offer price of `;

        disclaimerText += `${offerPrice} at ${annualMiles} mi/yr for ${formattedTermMonths} with ${formattedDownPayment} ${downPaymentQualifier} for qualified buyers. ${offerPriceChange}Terms may vary.`;
    } else {
        disclaimerText = 'Final transaction may include title, registration, license and other additional fees/charges.';
    }

    if (data.programEndDate && data.offerType !== 'cash') {
        const programEndDate = formatDate(new Date(data.programEndDate));
        const availableThroughText = data.boldDealValues
            ? ` <b>Available through ${programEndDate}.</b>`
            : ` Available through ${programEndDate}.`;
        disclaimerText = `${disclaimerText}${availableThroughText}`;
    }

    return disclaimerText;
}

export function capitalize(text: string): string {
    if (text) {
        return text.length ? text[0].toLocaleUpperCase() + text.substr(1) : text;
    } else {
        return text;
    }
}

export function maskString(value: string, prefixLength: number = 1): string {
    const length = (value && value.length) || 0;
    if (length <= prefixLength || prefixLength <= 0) {
        return value;
    }
    const prefix = value.substring(0, prefixLength);
    const suffix = '*'.repeat(length - prefixLength);
    const obscured = `${prefix}${suffix}`;
    return obscured;
}

export function maskDomain(domain: string): string {
    const domainIsValid = validateDomain(domain);
    if (!domainIsValid) {
        throw new Error(`Invalid domain ${domain} passed to maskDomain!`);
    }
    const [prefix, suffix] = domain.split('.');
    const maskDomain = `${maskString(prefix)}.${suffix}`;
    return maskDomain;
}

export function maskEmail(email: string): string {
    const emailIsValid = validateEmail(email);
    if (!emailIsValid) {
        throw new Error(`Invalid email ${email} passed to maskEmail!`);
    }
    const [prefix, suffix] = email.split('@');
    const obscuredEmail = `${maskString(prefix, 2)}@${maskDomain(suffix)}`;
    return obscuredEmail;
}

/**
 * This function create the verbiage for the taxes and fees disclaimer
 * Taxes and fees disclaimer uses the payment data to determine the verbiage
 * @return {String} disclaimer verbiage
 * @param {ILongDisclaimerData} terms data
 */
export function getTaxesAndFeesDisclaimer(data: ILongDisclaimerData) {
    let disclaimer = '';

    if (data.feesDisclaimer) {
        disclaimer += `${data.feesDisclaimer} `;
    }

    if (data.totalTax && data.hasDealerFees) {
        disclaimer += 'Includes tax, title, registration, and license fees.';
    } else if (!data.totalTax && !data.hasDealerFees) {
        disclaimer += 'Excludes tax, title, registration, and license fees.';
    } else if (data.totalTax && !data.hasDealerFees) {
        disclaimer += 'Includes tax. Excludes title, registration, and license fees.';
    } else if (!data.totalTax && data.hasDealerFees) {
        disclaimer += 'Excludes tax. Includes title, registration, and license fees.';
    }

    return disclaimer.trim();
}

// removes all non alpha-numeric characters and replaces spaces with -
export const kebabCase = (str: string) => str.replace(/[^a-z0-9+]+/gi, '-').toLowerCase();

export const roundToTwoDecimal = (numberNeedingRounding: number): number => {
    return Number.parseFloat(numberNeedingRounding.toFixed(2));
};

// creates a masking array for numeric input
export const createNumberMask = (rawValue: string) => {
    const mask = rawValue.split('').map((char) => (/\d/.test(char) ? /\d/ : char));

    const dotIndex = mask.indexOf('.');
    if (dotIndex !== -1) {
        mask.length = Math.min(mask.length, dotIndex + 3);
    }
    return mask;
};

/**
 * Sanitizes address fields by removing special characters that don't meet FS API regex requirements
 * and trimming trailing spaces
 * @param address: string;
 * @returns string
 */
export const createSafeAddress = (address: string): string => {
    if (!address) {
        return '';
    }

    // First remove common symbols that we don't want before normalization
    // This prevents symbols like ™ from being converted to "TM" during normalization
    const sanitizedAddress = address
        .trim()
        .replace(/[©®™@&%]/g, '') // Remove common symbols first
        .normalize('NFKD') // Then normalize unicode characters
        .replace(/[^a-zA-Z0-9 ,.'\-#]/g, '') // Keep only allowed characters
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .trim(); // Trim again after space normalization

    return sanitizedAddress;
};

/**
 * Safely formats a number as a percentage string.
 *
 * - Scales values ≤ 1 (e.g., 0.75 → "75%")
 * - Leaves values > 1 unscaled (e.g., 75 → "75%")
 * - Handles invalid inputs gracefully
 *
 * @param {number|string} value - The input value.
 * @param {string} defaultString - Returned on invalid input (default: "0%").
 * @param {number} decimals - Decimal places (default: 0).
 * @returns {string} Formatted percent string.
 */
export const formatPercentage = (value: number | string, defaultString: string = '0%', decimals: number = 0) => {
    const instance = numeral(value);
    const num = instance.value();

    if (num == null || isNaN(num)) {
        return defaultString;
    }

    const decimalPlaces = Math.max(0, decimals);
    const percentFormat = '0.' + '0'.repeat(decimalPlaces) + '%';
    const fixedFormat = '0.' + '0'.repeat(decimalPlaces);

    // If value is ≤ 1, treat as decimal (scale it)
    return num <= 1 ? numeral(num).format(percentFormat) : numeral(num).format(fixedFormat) + '%';
};
